import { emailError, mobileError, DEFAULT_DIAL_CODE } from '@aiinterview/shared';

/**
 * What a candidate form must contain before it can be saved.
 *
 * Kept out of the component so it can be tested as what it is - a function from a form to a list of
 * problems - and so the rules can be read in one place rather than inferred from JSX. The rules
 * themselves live in shared/candidateRules, because candidates.ts applies the identical ones to the
 * request; this file only says which fields a form has and which of them are required.
 */
export interface CandidateForm {
    fullName: string;
    email: string;
    /** The country dialling code, digits only and without the plus: "91". */
    dialCode: string;
    /** The national number, ten digits and nothing else. */
    mobile: string;
    externalRef: string;
    position: string;
    notes: string;
}

export const EMPTY_CANDIDATE: CandidateForm = {
    fullName: '',
    email: '',
    dialCode: DEFAULT_DIAL_CODE,
    mobile: '',
    externalRef: '',
    position: '',
    notes: ''
};

export type CandidateFieldError = Partial<Record<keyof CandidateForm | 'resume', string>>;

/**
 * The problems with a candidate form, keyed by field.
 *
 * Every one of these is required on both screens - adding by hand and editing - by decision. That
 * has a cost worth knowing: a candidate who arrived through a CSV import or the public API has
 * whatever those routes asked for, often a name and nothing else, and their record cannot be saved
 * from this screen until the gaps are filled. Those two intake routes keep their own, looser
 * schemas, so importing itself still works exactly as it did.
 */
export function candidateErrors(form: CandidateForm, hasResume: boolean): CandidateFieldError {
    const errors: CandidateFieldError = {};

    if (!form.fullName.trim()) {
        errors.fullName = 'A full name is required.';
    }

    const email = emailError(form.email);
    if (email) errors.email = email;

    if (!form.dialCode.trim()) {
        errors.dialCode = 'Choose a country code.';
    }

    const mobile = mobileError(form.mobile);
    if (mobile) errors.mobile = mobile;

    if (!form.position.trim()) {
        errors.position = 'The role applied for is required.';
    }

    if (!hasResume) {
        errors.resume = 'A resume is required.';
    }

    return errors;
}
